import { ActiveStatusSection } from "./ActiveStatusSection";
import { RequisitionsSection } from "./RequisitionsSection";
import { RevenueSection } from "./RevenueSection";
import { Column } from "@/components";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { cn } from "@/lib/utils";
import {
  CandidacyPlacement,
  Job,
  JobStatus,
  Requisition,
  RequisitionStatus,
} from "@/types";

import { JobSchema } from "../JobForm";

export const JobUpdateConfirmationDialog = ({
  companyId,
  formValues,
  isError,
  isLoading,
  onConfirm,
  onOpenChange,
  open,
  originalJob,
  placements,
  requisitions,
}: {
  companyId: string;
  formValues: JobSchema;
  isError: boolean;
  isLoading: boolean;
  onConfirm: () => void;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  originalJob?: Job;
  placements: CandidacyPlacement[];
  requisitions: Requisition[];
}) => {
  const handleOpenChange = (value: boolean) => {
    onOpenChange(value);
  };

  // Diff computation
  const isGoingActive =
    formValues.status === JobStatus.ACTIVE &&
    (originalJob === undefined || originalJob.status !== JobStatus.ACTIVE);

  const hasShiftTypeChanged = originalJob
    ? [...formValues.shiftType].sort().join(",") !==
      [...originalJob.shiftType].sort().join(",")
    : false;

  const hasShiftDiffChanged = originalJob
    ? formValues.shiftDifferentialMin !== originalJob.shiftDifferentialMin ||
      formValues.shiftDifferentialMax !== originalJob.shiftDifferentialMax
    : false;

  const hasShiftChanges = hasShiftTypeChanged || hasShiftDiffChanged;

  const hasCompChanges = originalJob
    ? formValues.compensationMin !== originalJob.compensationMin ||
      formValues.compensationMax !== originalJob.compensationMax
    : false;

  const hasBackfillChanged = originalJob
    ? formValues.backfillGuaranteeDays !== originalJob.backfillGuaranteeDays
    : false;

  const hasRevenueAmountChanged = originalJob
    ? formValues.estimatedRevenueAmount !== originalJob.estimatedRevenueAmount
    : false;

  const hasRevenueChanges = hasBackfillChanged || hasRevenueAmountChanged;

  const affectedRequisitions = requisitions.filter((r) =>
    [
      RequisitionStatus.LIVE,
      RequisitionStatus.PENDING,
      RequisitionStatus.BACKFILL,
    ].includes(r.status),
  );

  const showRequisitionsSection =
    hasShiftChanges && affectedRequisitions.length > 0 && originalJob;
  const showPlacementsSection =
    (hasCompChanges || hasRevenueChanges) &&
    placements.length > 0 &&
    originalJob;
  const hasTableSections =
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    showRequisitionsSection || showPlacementsSection;

  return (
    <Dialog onOpenChange={handleOpenChange} open={open}>
      <DialogContent
        className={cn(
          "flex max-h-[80vh] flex-col gap-4 overflow-y-auto",
          hasTableSections ? "max-w-5xl" : "max-w-3xl",
        )}
        onKeyDown={(e) => {
          if (e.key === "Enter" && e.metaKey && !e.repeat && !isLoading) {
            e.preventDefault();
            onConfirm();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold text-slate-700">
            Review before confirming
          </DialogTitle>
        </DialogHeader>
        <Column className="gap-6">
          {isGoingActive && (
            <ActiveStatusSection originalStatus={originalJob?.status} />
          )}
          {showRequisitionsSection && (
            <>
              {isGoingActive && <hr className="border-slate-200" />}
              <RequisitionsSection
                affectedRequisitions={affectedRequisitions}
                hasShiftTypeChanged={hasShiftTypeChanged}
                originalJob={originalJob}
                formValues={formValues}
              />
            </>
          )}
          {showPlacementsSection && (
            <>
              {(isGoingActive || showRequisitionsSection) && (
                <hr className="border-slate-200" />
              )}
              <RevenueSection
                placements={placements}
                originalJob={originalJob}
                formValues={formValues}
                companyId={companyId}
                hasBackfillChanged={hasBackfillChanged}
                hasRevenueAmountChanged={hasRevenueAmountChanged}
                hasCompChanges={hasCompChanges}
              />
            </>
          )}
        </Column>
        {isError && (
          <p className="text-sm text-red-600">
            An error occurred while saving. Please try again.
          </p>
        )}
        <DialogFooter className="border-t border-slate-200 pt-4">
          <Button
            autoFocus
            className="self-end rounded-full focus:ring-2 focus:ring-slate-400 focus:ring-offset-2"
            data-confirmation-cancel="true"
            disabled={isLoading}
            onClick={() => {
              handleOpenChange(false);
            }}
            variant="secondary"
          >
            Cancel
          </Button>
          <Button
            className="self-end rounded-full"
            disabled={isLoading}
            onClick={() => {
              onConfirm();
            }}
          >
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
